import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/providers/tenant-context';
export default function CsaeStandardsPage() {
  const { tenant, isWhitelabel } = useTenant();

  return (
    <div className="flex flex-col min-h-screen bg-background relative overflow-x-hidden transition-colors duration-300 font-sans">
      {/* Background Ambience */}
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="fixed inset-0 w-full h-full -z-10 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] pointer-events-none" />

      {/* Navigation */}
      <header className="fixed top-0 w-full z-50 border-b border-border/10 bg-background/60 backdrop-blur-xl">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link className="flex items-center gap-2 group" to="/">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-10 h-10 object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <span className="font-bold text-2xl tracking-tighter text-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" className="rounded-full gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-32 pb-20">
        <div className="container px-6 mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-12"
          >
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-destructive/10 border border-destructive/20 text-xs font-semibold text-destructive uppercase tracking-wider">
                <ShieldAlert className="h-3 w-3" />
                <span>Safety & Protection</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-foreground">
                Child Sexual Abuse and Exploitation (CSAE) Standards
              </h1>
              <p className="text-muted-foreground text-lg">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>

            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">1. Zero Tolerance Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Milora has a strict, zero-tolerance policy against any form of Child Sexual Abuse
                  Material (CSAM) or the exploitation and abuse of children. We are deeply committed
                  to ensuring our platform is a safe space, and we condemn all behaviors that
                  threaten the safety or well-being of minors.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  2. Content Moderation & Detection
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We employ rigorous moderation policies and technologies designed to actively
                  detect, remove, and prevent the sharing of CSAM across our servers. Our proactive
                  approach involves combining automated scanning tools with human moderation
                  reviews.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">3. Reporting and Enforcement</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If any user is found uploading, sharing, or disseminating CSAM or engaging in
                  child exploitation, their account will be immediately and permanently terminated
                  without notice. We maintain the right to preserve relevant user records and
                  content as evidence.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  4. Cooperation with Authorities
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We are legally and morally obligated to report any instances of CSAM to the
                  appropriate authorities. In the United States, we report these violations directly
                  to the National Center for Missing & Exploited Children (NCMEC) via the
                  CyberTipline. We also work closely with international law enforcement agencies
                  whenever required.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  5. How to Report Suspected CSAE
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Community vigilance is critical. If you discover CSAM or witness the grooming or
                  exploitation of a minor on our platform, please report it immediately to our
                  dedicated Trust and Safety team at:
                </p>
                <div className="bg-muted p-4 rounded-lg font-mono text-sm inline-block">
                  simpliumtechnologies@gmail.com
                </div>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Please provide any relevant URLs, screenshots, or context to help us expedite our
                  investigation, but{' '}
                  <strong>do not download, save, or voluntarily share the illicit material</strong>{' '}
                  during your report.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border/40 bg-muted/20">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-8 w-auto object-contain grayscale opacity-80"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-8 h-8 object-contain grayscale opacity-80"
                />
                <span className="font-bold text-lg text-muted-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground/60">
            © {new Date().getFullYear()} Simplium Technologies LLP
          </p>
        </div>
      </footer>
    </div>
  );
}
